import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useDatabaseContent } from '@databases/database-state';
import { DRAG_HANDLER_TD_ELEMENT_WIDTH } from '@databases/icons';
import Z_INDEX from '@databases/layout/z-index';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
// eslint-disable-next-line no-restricted-imports -- reactive Redux selector required for per-row delete hover state; SelectionsReader is non-reactive
import { selectIsHoverDeleteEntriesButton } from '@databases/redux/cellSelections/selectors';
import {
  selectedBorderHoverDelete,
  VISUAL_REFRESH_TABLE_BORDER_RADIUS,
  visualRefreshSelectBorder,
} from '@databases/styles';
import {
  COMPACT_NUMBERED_COLUMN_WIDTH,
  NATIVE_EMBED_DRAG_HANDLE_SPACE,
  NUMBERED_COLUMN_WIDTH,
  TABLE_CLASSNAMES,
} from '@databases/utils/constants';
import useView from '@databases/views/useView';
import classNames from 'classnames';
import React, { type FunctionComponent, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { shouldShowEntryPrefix } from '../../../utils';

import EntryCheckbox from './EntryCheckbox';
import SelectionsEntryPrefixContent from './SelectionsEntryPrefixContent';

const FirstCellContentContainer = xcss({
  height: token('space.250', '20px'),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const compactEntryPrefixContainer = xcss({
  height: token('space.250', '20px'),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const checkboxSlotContainer = xcss({
  minWidth: token('space.300', '24px'),
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const StyledEntryPrefixTd = styled.td<{
  showEntryPrefix: boolean;
  isSelected: boolean;
  isLastEntry: boolean;
  entryBeforeIsSelected?: boolean;
}>`
  position: relative;
  width: ${(props) =>
    props.showEntryPrefix
      ? isNativeEmbedEditorPlugin()
        ? COMPACT_NUMBERED_COLUMN_WIDTH + NATIVE_EMBED_DRAG_HANDLE_SPACE
        : NUMBERED_COLUMN_WIDTH
      : 0}px;
  left: ${() => (isNativeEmbedEditorPlugin() ? 0 : DRAG_HANDLER_TD_ELEMENT_WIDTH / 2)}px;

  ${({ isSelected }) =>
    isNativeEmbedEditorPlugin() &&
    css`
      box-sizing: border-box;
      overflow: ${isSelected ? 'visible' : 'hidden'};
      padding: ${token('space.025', '4px')};
    `}

  visibility: ${(props) => (props.showEntryPrefix ? 'visible' : 'hidden')};
  justify-content: start;
  align-items: center;
  background: ${token('elevation.surface', '#FFFFF')};
  z-index: ${Z_INDEX.TABLE_NUMBERED_COLUMN};
  color: ${token('color.text.subtlest', '#6B778C')};

  // Without this entries will grow larger due to targeted css from TableLayout.
  ${({ showEntryPrefix }) =>
    !showEntryPrefix &&
    css`
      height: 0;
    `}

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${token('color.background.accent.blue.subtlest', '#E9F2FF')};

      &.${TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON} {
        && {
          transition: background-color 0.2s;
          background: ${token('color.background.danger.hovered', '#FFD5D2')};
        }
      }
    `}

  ${({ isSelected, entryBeforeIsSelected }) =>
    isNativeEmbedEditorPlugin() &&
    isSelected &&
    !entryBeforeIsSelected &&
    css`
      border-top: ${visualRefreshSelectBorder};
    `}
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const EntryPrefixBorder = styled.div<{
  entryBeforeIsSelected: boolean;
  entryAfterIsSelected: boolean;
  isFirstEntry: boolean;
  isLastEntry: boolean;
  borderStyle: string;
  isReadOnly: boolean;
}>`
  position: absolute;
  top: ${(props) => (props.isFirstEntry ? token('space.025', '2px') : '0')};
  left: 0;
  bottom: 0;
  right: 0;
  border: ${(props) => props.borderStyle};
  border-top: ${(props) => (props.entryBeforeIsSelected ? 'none' : props.borderStyle)};
  border-bottom: ${(props) => (props.entryAfterIsSelected ? 'none' : props.borderStyle)};
  border-right: none;
  border-start-start-radius: ${(props) =>
    !props.entryBeforeIsSelected && !isNativeEmbedEditorPlugin() ? '3px' : '0'};
  border-end-start-radius: ${(props) =>
    !props.entryAfterIsSelected && !isNativeEmbedEditorPlugin() ? '3px' : '0'};
  ${(props) =>
    props.isReadOnly &&
    props.isLastEntry &&
    `
      border-end-start-radius: ${VISUAL_REFRESH_TABLE_BORDER_RADIUS};
    `}
  margin-top: ${(props) => (props.isFirstEntry ? '0px' : '-1px')};
  margin-left: ${() => (isNativeEmbedEditorPlugin() ? 0 : -1)}px;
  margin-bottom: ${(props) => (props.isLastEntry ? '0px' : '-1px')};
  margin-right: ${token('space.negative.025', '-2px')};
`;

export type EntryIsSelectedMap = Record<string, boolean>;

export interface EntryPrefixProps {
  contentId: string;
  entryId: string;
  entryIsSelectedMap: EntryIsSelectedMap;
  index: number;
  isFirstEntry: boolean;
  isLastEntry: boolean;
  isSelected: boolean;
  setEntryHover: (isHovered: boolean) => void;
  isReadOnly: boolean;
  dragHandle?: React.ReactNode;
}

const EntryPrefix: FunctionComponent<EntryPrefixProps> = ({
  contentId,
  entryId,
  entryIsSelectedMap,
  index,
  isFirstEntry,
  isLastEntry,
  isSelected,
  setEntryHover,
  isReadOnly,
  dragHandle,
}) => {
  const { referencing } = useDatabaseContent();
  const isHoverDeleteEntriesButton = defaultStoreUtils.useEqualSelector(
    (state) => isSelected && selectIsHoverDeleteEntriesButton(state)
  );
  const { view } = useView();
  const showEntryPrefix = shouldShowEntryPrefix(view);
  const [isHovered, setIsHovered] = useState(false);

  const getEntrySelectionStatus = useCallback(() => {
    const entryIds = Object.keys(entryIsSelectedMap);
    const entryIndex = entryIds.indexOf(entryId);
    if (entryIndex === -1) {
      return { entryBeforeIsSelected: false, entryAfterIsSelected: false };
    }
    const entryBeforeId = entryIds[entryIndex - 1];
    const entryAfterId = entryIds[entryIndex + 1];
    return {
      entryBeforeIsSelected: entryIsSelectedMap[entryBeforeId] ?? false,
      entryAfterIsSelected: entryIsSelectedMap[entryAfterId] ?? false,
    };
  }, [entryId, entryIsSelectedMap]);

  const { entryBeforeIsSelected, entryAfterIsSelected } = getEntrySelectionStatus();

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setEntryHover(true);
  }, [setEntryHover]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setEntryHover(false);
  }, [setEntryHover]);

  const borderStyle = isHoverDeleteEntriesButton
    ? selectedBorderHoverDelete
    : visualRefreshSelectBorder;

  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  const shouldShowTelepointers = !isSelected && !isReadOnly;

  const { participants } = usePresence({
    grouping: { entryId },
    componentId: 'EntryPrefix',
    isEnabled: shouldShowTelepointers,
  });

  //Used to determine whether we render the checkbox or the number in the first column
  const shouldRenderNumber = view.settings.layout.showNumberedEntries;
  const shouldRenderCheckbox = useCallback(
    (isEntrySelected: boolean) =>
      isPluginEnabled(PluginKey.CELL_SELECTION) &&
      (isHovered || isEntrySelected || !shouldRenderNumber),
    [isHovered, shouldRenderNumber]
  );

  const nameplateRef = useRef<HTMLTableCellElement>(null);
  const hasNativeEmbedDragHandle = dragHandle && isNativeEmbedEditorPlugin();
  const cellContent = isSelectionsPerformanceEnabled ? (
    <SelectionsEntryPrefixContent
      entryId={entryId}
      index={index}
      shouldRenderCheckbox={shouldRenderCheckbox}
    />
  ) : shouldRenderCheckbox(isSelected) ? (
    <EntryCheckbox
      contentId={contentId}
      entryId={entryId}
      isSelected={isSelected}
      referencing={referencing}
      index={index}
    />
  ) : (
    index + 1
  );

  return (
    <StyledEntryPrefixTd
      className={classNames('entry-number-table-data', {
        [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
        [TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON]: isHoverDeleteEntriesButton,
      })}
      showEntryPrefix={showEntryPrefix}
      isSelected={isSelected}
      isFirstEntry={isFirstEntry}
      isLastEntry={isLastEntry}
      entryBeforeIsSelected={entryBeforeIsSelected}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={nameplateRef}
    >
      {shouldShowTelepointers && isHovered && (
        <PresenceNameplate participants={participants} anchorRef={nameplateRef} />
      )}
      {isSelected && (
        <EntryPrefixBorder
          className="entry-number-table-border"
          entryBeforeIsSelected={entryBeforeIsSelected}
          entryAfterIsSelected={entryAfterIsSelected}
          isFirstEntry={isFirstEntry}
          isLastEntry={isLastEntry}
          borderStyle={borderStyle}
          isReadOnly={isReadOnly}
        />
      )}
      <Box
        xcss={
          hasNativeEmbedDragHandle
            ? compactEntryPrefixContainer
            : FirstCellContentContainer
        }
      >
        {dragHandle}
        {hasNativeEmbedDragHandle ? (
          <Box xcss={checkboxSlotContainer}>{cellContent}</Box>
        ) : (
          cellContent
        )}
      </Box>
    </StyledEntryPrefixTd>
  );
};

export default EntryPrefix;
